import React from 'react';
import { Transition } from 'react-transition-group';

import { bemBlock } from '../../modules/bem';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { Layout } from '../layout/Layout';
import { Section } from '../section/Section';

import './Collapse.less';

const block = bemBlock('collapse');

type CollapseProps = Omit<React.ComponentProps<typeof Section>, 'title'> & {
  wrap?: React.ComponentProps<typeof Layout.Row>['wrap'];
  icon?: React.ComponentProps<typeof Icon>['glyph'];
  children: React.ReactNode;
  extra?: React.ReactNode;
  disableUnmountOnExit?: boolean;
  disableMountOnEnter?: boolean;
  title: React.ReactNode;
  toggleButtonProps?: React.ComponentProps<typeof Button>;
  onToggle?: () => void;
  collapsed?: boolean;
  toggleVariant?: 'full-width' | 'icon-only';
  id: string;
  contentClassName?: string;
  disableAnimation?: boolean;
  contentProps?: React.ComponentProps<'div'>;
  timeout?: { appear?: number; enter?: number; exit?: number };
};

const CollapseBase = (
  {
    id,
    title,
    icon = 'chevron-down',
    onToggle,
    collapsed,
    extra,
    wrap,
    children,
    disableMountOnEnter,
    disableUnmountOnExit,
    toggleVariant = 'full-width',
    className,
    contentClassName,
    toggleButtonProps,
    disableAnimation,
    contentProps,
    timeout,
    ...props
  }: CollapseProps,
  ref: React.Ref<HTMLDivElement>,
) => {
  const renderHeader = () => {
    if (!onToggle) {
      return title;
    }

    if (toggleVariant === 'full-width') {
      return (
        <Button
          aria-expanded={!collapsed}
          className={block({ element: 'toggle', modifiers: { 'full-width': true } })}
          variant="transparent"
          data-role="collapse-toggle-button"
          onClick={onToggle}
          aria-controls={id}
          withPadding="none"
          size="w-full"
          {...toggleButtonProps}
        >
          <Layout.Row alignItems="center">
            <Icon
              className={block({ element: 'chevron', modifiers: { collapsed } })}
              color="text-subdued"
              glyph={icon}
              fixedWidth
              size="lg"
            />
            {title}
          </Layout.Row>
        </Button>
      );
    } else if (toggleVariant === 'icon-only') {
      return (
        <Layout.Row alignItems="center" justifyContent="start">
          <Button
            aria-expanded={!collapsed}
            className={block('toggle')}
            variant="transparent"
            data-role="collapse-toggle-button"
            onClick={onToggle}
            aria-controls={id}
            withPadding="none"
            square
            size="lg"
            {...toggleButtonProps}
          >
            <Icon
              className={block({ element: 'chevron', modifiers: { collapsed } })}
              color="text-subdued"
              glyph={icon}
              fixedWidth
              size="lg"
            />
          </Button>
          {title}
        </Layout.Row>
      );
    }
  };

  return (
    <Section
      elementRef={ref}
      {...props}
      className={block({ modifiers: { collapsed }, extra: className })}
    >
      <Layout.Row
        className={block({ element: 'panel', modifiers: { collapsed } })}
        spacedChildren="sm"
        alignItems="center"
        span="shrink"
        wrap={wrap}
      >
        <Layout.Row span="grow" alignItems="center" justifyContent="start">
          {renderHeader()}
        </Layout.Row>
        <Layout.Row span="shrink" justifyContent="end">
          {extra}
        </Layout.Row>
      </Layout.Row>
      <Transition
        exit
        appear
        in={!collapsed}
        mountOnEnter={!disableMountOnEnter}
        unmountOnExit={!disableUnmountOnExit}
        timeout={{ exit: 250, enter: 0, ...timeout }}
      >
        {(state) => (
          <div
            id={id}
            className={block({
              element: 'content',
              modifiers: { state, 'with-transition': !disableAnimation },
              extra: contentClassName,
            })}
            {...contentProps}
          >
            <div>{children}</div>
          </div>
        )}
      </Transition>
    </Section>
  );
};

export const Collapse = React.forwardRef<HTMLDivElement, CollapseProps>(CollapseBase);
